import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import Price from '@/components/ui/Price';
import { selectGuestToken } from './tableSessionSlice';
import { useVerifyTablePaymentMutation } from './tableSessionApi';

const RETRY_MS = 3000;
// Stancer peut mettre quelques secondes à basculer la transaction. Au-delà,
// c'est le filet de rattrapage serveur (reconcile_pending_payments) qui
// encaissera : on arrête de faire tourner le téléphone du client.
const MAX_RETRIES = 6;

type State = 'verifying' | 'paid' | 'paid_partial' | 'pending' | 'failed';

/**
 * Retour de la page hébergée Stancer, pour le paiement autonome d'une table.
 *
 * L'URL est composée SERVEUR (`build_return_url`) : `?tsp=<uuid du paiement>`.
 * On ne fait que rejouer la vérification — le client ne porte aucune preuve de
 * paiement, c'est le serveur qui interroge Stancer.
 *
 * **La règle qui gouverne tout cet écran : on ne dit « échec » que sur un
 * verdict du serveur.** Un `failed` / `expired` renvoyé par le backend est une
 * réponse de Stancer, donc une preuve que rien n'a été débité. Une coupure
 * réseau, un 401, un 502 ne prouvent rien du tout — l'argent peut très bien
 * être parti. Confondre les deux pousse le convive à payer une seconde fois,
 * et c'est précisément l'accident que le filet de rattrapage serveur existe
 * pour éviter. Dans le doute, on dit « en cours de validation ».
 */
export default function TablePaymentReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantUuid = '', tableToken = '' } = useParams<{
    restaurantUuid: string;
    tableToken: string;
  }>();
  const [searchParams] = useSearchParams();
  const paymentUuid = searchParams.get('tsp') ?? '';
  const guestToken = useAppSelector(selectGuestToken);
  const [verifyPayment] = useVerifyTablePaymentMutation();
  const [state, setState] = useState<State>('verifying');
  const [remainingCents, setRemainingCents] = useState(0);
  const [paidCents, setPaidCents] = useState(0);
  const attemptsRef = useRef(0);

  const backToTable = `/${restaurantUuid}/table/${tableToken}`;

  useEffect(() => {
    // Sans identifiant de paiement ou sans jeton de table, on ne peut RIEN
    // demander — donc rien affirmer. « en attente » et non « échec » : le
    // débit a pu passer, et le filet serveur l'imputera.
    if (!paymentUuid || !guestToken) {
      setState('pending');
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    async function run() {
      try {
        const result = await verifyPayment({ guestToken: guestToken!, paymentUuid }).unwrap();
        if (cancelled) return;
        if (result.status === 'paid') {
          // « Encaissé » et « table réglée » sont deux faits différents : une
          // tournée arrivée entre-temps, ou une capture partielle, laisse un
          // solde. Annoncer « table réglée » ferait de ce écran une quittance
          // fausse.
          const due = Math.round(parseFloat(result.due_total ?? '0') * 100) || 0;
          setRemainingCents(due);
          setPaidCents(Math.round(parseFloat(result.paid_total ?? '0') * 100) || 0);
          setState(result.is_paid && due <= 0 ? 'paid' : 'paid_partial');
          return;
        }
        // Seul verdict qui autorise « rien n'a été débité ».
        if (result.status === 'failed' || result.status === 'expired') {
          setState('failed');
          return;
        }
        attemptsRef.current += 1;
        setState('pending');
        if (attemptsRef.current < MAX_RETRIES) {
          retryTimer = setTimeout(run, RETRY_MS);
        }
      } catch {
        if (cancelled) return;
        // Erreur de transport : on n'a PAS de verdict. On retente, puis on
        // s'arrête sur « en cours », jamais sur « échec ».
        attemptsRef.current += 1;
        if (attemptsRef.current < MAX_RETRIES) {
          setState('verifying');
          retryTimer = setTimeout(run, RETRY_MS);
          return;
        }
        setState('pending');
      }
    }

    run();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [paymentUuid, guestToken, verifyPayment]);

  return (
    <div className="max-w-[600px] mx-auto px-4 py-12 text-center font-mono space-y-4">
      {state === 'verifying' && <p className="text-ink/70">{t('tablePayment.returnVerifying')}</p>}
      {state === 'paid' && (
        <p data-testid="table-payment-return-paid" className="text-green-700 font-display text-lg">
          {t('tablePayment.returnPaid')}
        </p>
      )}
      {state === 'paid_partial' && (
        <div data-testid="table-payment-return-partial" className="space-y-1">
          <p className="m-0 text-green-700 font-display text-lg">
            {t('tablePayment.returnPaidPartial')}
          </p>
          {/* Le solde seul laisse croire que le paiement n'a pas compte : on
              rappelle d'abord ce qui est encaisse sur la table. */}
          {paidCents > 0 && (
            <p data-testid="table-payment-return-paid-total" className="m-0 text-ink/70">
              {t('tablePayment.paidLabel')} <Price amountCents={paidCents} />
            </p>
          )}
          <p className="m-0 text-ink/70">
            {t('tablePayment.returnRemaining')} <Price amountCents={remainingCents} />
          </p>
        </div>
      )}
      {state === 'pending' && (
        <div data-testid="table-payment-return-pending" className="space-y-1">
          <p className="m-0 text-ink/70">{t('tablePayment.returnPending')}</p>
          <p className="m-0 text-ink/60 text-sm">{t('tablePayment.returnPendingHint')}</p>
        </div>
      )}
      {state === 'failed' && (
        <>
          <p data-testid="table-payment-return-failed" className="text-sale-red">
            {t('tablePayment.returnFailed')}
          </p>
          <p className="text-ink/70">{t('tablePayment.returnFailedHint')}</p>
        </>
      )}
      {state !== 'verifying' && (
        <button
          type="button"
          data-testid="table-payment-return-back"
          onClick={() => navigate(backToTable)}
          className="mt-2 bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-5 py-3 font-display shadow-stamp-sm"
        >
          {t('tablePayment.returnBack')}
        </button>
      )}
    </div>
  );
}
